import React, { SyntheticEvent } from 'react';
import Input from '../../../components/Input';
import BankCodeDropdown from '../../../components/BankCodeDropdown';
import SectionLabel from '../../../components/SectionLabel';
import { Checkbox, Grid } from 'semantic-ui-react';
import { Field } from 'formik';
import { FormikErrorMessage } from '../../../components/ErrorMessage';
import { FieldLabel } from '../../../styles';
import {
  formatPercentageValue,
  isPercentageAboveMaximum,
  maskPercentageValue
} from '../../../utils/percentage';
import { dangerNotification } from '../../../services/notification';
import { EstateFormProps } from '../UpdateEstateForm';
import { UserRolesEnum } from '../../../enums/user-roles.enum';
import Button from '../../../components/Button';
import { useHistory } from 'react-router-dom';
import { useStandaloneChargeAccountStatus } from '../../standalone-charges/hooks/useStandaloneChargeAccountStatus';

export const BankAccountData = (props: EstateFormProps) => {
  const { errors, touched, handleInputChange, setFieldValue, values, initialValues } =
    props;
  const canViewBillingConfiguration =
    props.user.role === UserRolesEnum.brokerAdmin ||
    props.user.role === UserRolesEnum.estateAdmin;
  const canEditOperationFee = props.user.role === UserRolesEnum.brokerAdmin;
  const history = useHistory();
  const chargesEnabledInForm = Boolean(values.standaloneChargesEnabled);
  const persistedChargesEnabled = Boolean(initialValues.standaloneChargesEnabled);
  const showChargesConnection = persistedChargesEnabled && chargesEnabledInForm;
  const needsSaveToEnableConnection =
    chargesEnabledInForm && !persistedChargesEnabled;
  const { connected, loading: accountLoading } = useStandaloneChargeAccountStatus(
    values.id,
    showChargesConnection
  );

  const handleSplitPercentBlur = () => {
    const splitPercent = values.bankAccount?.standaloneChargesSplitPercent;

    if (isPercentageAboveMaximum(splitPercent)) {
      dangerNotification(
        'Taxa de cobrança',
        'A taxa de cobrança não pode ser maior que 100,00.'
      );
      return;
    }

    setFieldValue(
      'bankAccount.standaloneChargesSplitPercent',
      formatPercentageValue(splitPercent)
    );
  };

  const handleCommissionPercentBlur = () => {
    if (isPercentageAboveMaximum(values.standaloneChargesCommissionPercent)) {
      dangerNotification(
        'Percentual Repassado',
        'O percentual repassado não pode ser maior que 100,00.'
      );
      return;
    }

    setFieldValue(
      'standaloneChargesCommissionPercent',
      formatPercentageValue(values.standaloneChargesCommissionPercent)
    );
  };
  const openChargesOnboarding = () => {
    history.push(`/admin/standalone-charges?estateId=${values.id}`);
  };
  return (
    <>
      <SectionLabel text="DADOS BANCÁRIOS" />

      <Grid.Row>
        <Grid.Column width={6}>
          <FieldLabel>Código do Banco*</FieldLabel>
          <Field
            name="bankAccount.bankCode"
            className={
              errors && errors.bankAccount && touched && touched.bankAccount
                ? 'text-input error'
                : 'text-input'
            }
            onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
            component={BankCodeDropdown}
            maxLength={3}
            fluid
          />
          <FormikErrorMessage component="div" name="bankAccount.bankCode" />
        </Grid.Column>

        <Grid.Column width={4}>
          <FieldLabel>Agência*</FieldLabel>
          <Field
            name="bankAccount.agencyNumber"
            className={
              errors && errors.bankAccount && touched && touched.bankAccount
                ? 'text-input error'
                : 'text-input'
            }
            onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
            component={Input}
            maxLength={4}
            fluid
          />
          <FormikErrorMessage component="div" name="bankAccount.agencyNumber" />
        </Grid.Column>

        <Grid.Column width={1} style={{ padding: 1 }}>
          <Field
            name="bankAccount.agencyDv"
            style={{ marginTop: '18px' }}
            className={
              errors && errors.bankAccount && touched && touched.bankAccount
                ? 'text-input error'
                : 'text-input'
            }
            onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
            component={Input}
            maxLength={1}
            fluid
          />
          <FormikErrorMessage component="div" name="bankAccount.agencyDv" />
        </Grid.Column>

        <Grid.Column width={4}>
          <FieldLabel>Conta*</FieldLabel>
          <Field
            name="bankAccount.accountNumber"
            className={
              errors && errors.bankAccount && touched && touched.bankAccount
                ? 'text-input error'
                : 'text-input'
            }
            onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
            component={Input}
            fluid
          />
          <FormikErrorMessage component="div" name="bankAccount.accountNumber" />
        </Grid.Column>

        <Grid.Column width={1} style={{ padding: 3 }}>
          <Field
            name="bankAccount.accountDv"
            style={{ marginTop: '18px' }}
            className={
              errors && errors.bankAccount && touched && touched.bankAccount
                ? 'text-input error'
                : 'text-input'
            }
            onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
            component={Input}
            maxLength={1}
            fluid
          />
          <FormikErrorMessage component="div" name="bankAccount.accountDv" />
        </Grid.Column>
      </Grid.Row>

      {canViewBillingConfiguration && (
        <>
          <SectionLabel text="INFORMAÇÕES DE COBRANÇA" />
          <Grid.Row>
            <Grid.Column width={16}>
              <p style={{ color: '#666', margin: 0 }}>
                Configure a conta onde deseja receber os valores das cobranças realizadas
                pelo painel.
              </p>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              <FieldLabel style={{ marginBottom: '5px' }}>
                {values.standaloneChargesEnabled
                  ? 'Envio de cobranças habilitado'
                  : 'Envio de cobranças desabilitado'}
              </FieldLabel>
              <Checkbox
                toggle
                checked={Boolean(values.standaloneChargesEnabled)}
                label="Habilitar envio de cobranças"
                onChange={(_, data) => {
                  const enabled = Boolean(data.checked);
                  setFieldValue('standaloneChargesEnabled', enabled);
                  if (!enabled) {
                    setFieldValue('standaloneChargesCommissionEnabled', false);
                  }
                }}
              />
            </Grid.Column>
            {chargesEnabledInForm && (
              <Grid.Column width={4}>
                <FieldLabel style={{ marginBottom: '5px' }}>
                  {values.standaloneChargesCommissionEnabled
                    ? 'Comissionamento habilitado'
                    : 'Comissionamento desabilitado'}
                </FieldLabel>
                <Checkbox
                  toggle
                  label="Habilitar comissionamento"
                  checked={Boolean(values.standaloneChargesCommissionEnabled)}
                  onChange={(_, data) =>
                    setFieldValue(
                      'standaloneChargesCommissionEnabled',
                      Boolean(data.checked)
                    )
                  }
                />
              </Grid.Column>
            )}
          </Grid.Row>

          {chargesEnabledInForm && (
            <Grid.Row>
              {needsSaveToEnableConnection && (
                <Grid.Column width={16}>
                  <p style={{ color: '#666', margin: '0 0 12px', fontSize: '0.9em' }}>
                    Salve o cadastro após habilitar cobranças para conectar a conta na
                    Pagar.me.
                  </p>
                </Grid.Column>
              )}

              {showChargesConnection && (
                <Grid.Column width={4}>
                  <FieldLabel>Conta bancária</FieldLabel>
                  <div style={{ background: '#f7f7f7', padding: 10, minHeight: 84 }}>
                    <strong>
                      {accountLoading
                        ? 'Consultando conta...'
                        : connected
                          ? 'Conta conectada.'
                          : 'Nenhuma conta atribuída.'}
                    </strong>
                    {!accountLoading && !connected && (
                      <>
                        <p style={{ color: '#777', fontSize: '0.85em' }}>
                          Clique no botão abaixo para acessar a página de cobranças e
                          realizar a configuração inicial.
                        </p>
                        <Button
                          type="button"
                          text="Conectar"
                          onClick={openChargesOnboarding}
                        />
                      </>
                    )}
                  </div>
                </Grid.Column>
              )}

              <Grid.Column width={showChargesConnection ? 5 : 4}>
                <FieldLabel>Taxa de operação (%)</FieldLabel>
                <Field
                  name="bankAccount.standaloneChargesSplitPercent"
                  onChange={(_: SyntheticEvent, data: any) =>
                    setFieldValue(
                      'bankAccount.standaloneChargesSplitPercent',
                      maskPercentageValue(data.value)
                    )
                  }
                  onBlur={handleSplitPercentBlur}
                  component={Input}
                  inputMode="decimal"
                  disabled={!canEditOperationFee}
                  fluid
                />
                <p style={{ margin: '6px 0 0', fontSize: '0.85em', color: '#666' }}>
                  Percentual retido pela plataforma sobre o valor de cada cobrança
                  realizada.*
                </p>
              </Grid.Column>

              {values.standaloneChargesCommissionEnabled && (
                <Grid.Column width={5}>
                  <FieldLabel>Percentual repassado (%)</FieldLabel>
                  <Field
                    name="standaloneChargesCommissionPercent"
                    onChange={(_: SyntheticEvent, data: any) =>
                      setFieldValue(
                        'standaloneChargesCommissionPercent',
                        maskPercentageValue(data.value)
                      )
                    }
                    onBlur={handleCommissionPercentBlur}
                    component={Input}
                    inputMode="decimal"
                    fluid
                  />
                  <p style={{ margin: '6px 0 0', fontSize: '0.85em', color: '#666' }}>
                    O percentual será repassado aos colaboradores válidos vinculados às
                    cobranças.
                  </p>
                </Grid.Column>
              )}
            </Grid.Row>
          )}
        </>
      )}
    </>
  );
};
